import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../../components/ui/card"
import { Button } from "../../components/ui/button"
import {
  Calendar,
  BookOpen,
  Target,
  RotateCcw,
  Loader2,
  CheckCircle,
  Clock,
  SkipForward,
} from "lucide-react"
import {
  getDailyTasks,
  generateDailyTasks,
  updateTaskStatus,
  type DailyTaskItem,
} from "../../api/aiTeam"

export default function DailyTasksPage() {
  const [tasks, setTasks] = useState<DailyTaskItem[]>([])
  const [date, setDate] = useState(new Date().toISOString().split("T")[0])
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)

  useEffect(() => {
    loadTasks()
  }, [date])

  async function loadTasks() {
    setLoading(true)
    try {
      const data = await getDailyTasks(date)
      setTasks(data.tasks ?? [])
    } catch (err) {
      console.error("Failed to load tasks:", err)
    } finally {
      setLoading(false)
    }
  }

  async function handleGenerate() {
    setGenerating(true)
    try {
      const data = await generateDailyTasks(date)
      setTasks(data.tasks ?? [])
    } catch (err) {
      console.error("Failed to generate tasks:", err)
    } finally {
      setGenerating(false)
    }
  }

  async function handleStatusChange(index: number, status: "done" | "skipped") {
    try {
      await updateTaskStatus({ date, taskIndex: index, status })
      setTasks((prev) => {
        const updated = [...prev]
        updated[index] = { ...updated[index], status }
        return updated
      })
    } catch (err) {
      console.error("Failed to update task:", err)
    }
  }

  const taskTypeIcons: Record<string, typeof BookOpen> = {
    lesson: BookOpen,
    practice: Target,
    retry: RotateCcw,
    review: BookOpen,
  }

  const taskTypeLabels: Record<string, string> = {
    lesson: "学习",
    practice: "练习",
    retry: "重做错题",
    review: "复习",
  }

  const taskTypeColors: Record<string, string> = {
    lesson: "bg-blue-100 text-blue-600",
    practice: "bg-green-100 text-green-600",
    retry: "bg-orange-100 text-orange-600",
    review: "bg-purple-100 text-purple-600",
  }

  const completedCount = tasks.filter((t) => t.status === "done").length
  const totalDuration = tasks.reduce((sum, t) => sum + t.duration, 0)
  const completedDuration = tasks
    .filter((t) => t.status === "done")
    .reduce((sum, t) => sum + t.duration, 0)

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    )
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">每日任务</h1>
          <p className="text-slate-500 mt-1">AI 为你定制的学习计划</p>
        </div>
        <Button onClick={handleGenerate} disabled={generating}>
          {generating ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              生成中...
            </>
          ) : (
            "生成今日任务"
          )}
        </Button>
      </div>

      {/* Date Selector */}
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            const d = new Date(date)
            d.setDate(d.getDate() - 1)
            setDate(d.toISOString().split("T")[0])
          }}
        >
          ← 前一天
        </Button>
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-slate-400" />
          <span className="font-medium">{date}</span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            const d = new Date(date)
            d.setDate(d.getDate() + 1)
            setDate(d.toISOString().split("T")[0])
          }}
        >
          后一天 →
        </Button>
      </div>

      {tasks.length > 0 ? (
        <>
          {/* Progress Summary */}
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-blue-600">
                    {completedCount}/{tasks.length}
                  </div>
                  <p className="text-sm text-slate-500">已完成任务</p>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600">
                    {completedDuration}
                  </div>
                  <p className="text-sm text-slate-500">已学习(分钟)</p>
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-600">
                    {totalDuration}
                  </div>
                  <p className="text-sm text-slate-500">总计(分钟)</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Task List */}
          <div className="space-y-3">
            {tasks.map((task, idx) => {
              const Icon = taskTypeIcons[task.type] ?? BookOpen
              return (
                <Card
                  key={idx}
                  className={`transition-all ${
                    task.status === "done" ? "opacity-60" : ""
                  }`}
                >
                  <CardContent className="py-4">
                    <div className="flex items-center gap-4">
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          taskTypeColors[task.type] ?? taskTypeColors.lesson
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                      </div>

                      <div className="flex-grow">
                        <div className="flex items-center gap-2">
                          <span
                            className={`text-xs px-2 py-0.5 rounded ${
                              taskTypeColors[task.type] ?? taskTypeColors.lesson
                            }`}
                          >
                            {taskTypeLabels[task.type] ?? task.type}
                          </span>
                          <span
                            className={
                              task.status === "done"
                                ? "line-through text-slate-400"
                                : "font-medium"
                            }
                          >
                            {task.title}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-xs text-slate-400">
                          <Clock className="w-3 h-3" />
                          <span>{task.duration}分钟</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {task.status === "pending" ? (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleStatusChange(idx, "done")}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              完成
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleStatusChange(idx, "skipped")}
                            >
                              <SkipForward className="w-4 h-4" />
                            </Button>
                          </>
                        ) : task.status === "done" ? (
                          <span className="text-sm text-green-600 flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" />
                            已完成
                          </span>
                        ) : (
                          <span className="text-sm text-slate-400">已跳过</span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">
                还没有今日任务
              </h3>
              <p className="text-slate-500 mb-6">
                点击"生成今日任务"按钮，AI 将为你安排今天的学习计划
              </p>
              <Button onClick={handleGenerate} disabled={generating}>
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    生成中...
                  </>
                ) : (
                  "生成今日任务"
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
